import React, {useMemo} from "react";
import moment from "moment";
import {Box, ButtonBase, Stack, Typography} from "@mui/material";
import {campaignColor, campaignRunsOn, campaignStart, campaignState} from "./calendarUtils";

const minutesOf = (time) => {
  const [hours, minutes] = String(time || "0:0").split(":");
  return (Number(hours) || 0) * 60 + (Number(minutes) || 0);
};

const SectionLabel = ({children}) => (
  <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: "#8491F7", textTransform: "uppercase"}}>
    {children}
  </Typography>
);

const HOUR_MARKS = [0, 6, 12, 18, 24];

const TodayRow = ({campaign, onSelect}) => {
  const color = campaignColor(campaign);
  const from = Math.min(minutesOf(campaign.startTime), 1440);
  const to = Math.min(Math.max(minutesOf(campaign.endTime), from + 20), 1440);
  return (
    <ButtonBase onClick={() => onSelect(campaign)} sx={{
      width: "100%", flexDirection: "column", alignItems: "stretch", gap: 0.75, textAlign: "left", p: 1.25,
      borderRadius: "14px", bgcolor: "#FAFAFD", transition: "background-color .15s ease",
      "&:hover": {bgcolor: "rgba(132, 145, 247, 0.12)"},
    }}>
      <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1}>
        <Stack direction={"row"} alignItems={"center"} gap={0.75} sx={{minWidth: 0}}>
          <Box sx={{width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: color.dot}}/>
          <Typography noWrap sx={{fontSize: 13, fontWeight: 700, color: "#23264D"}}>{campaign.name}</Typography>
        </Stack>
        <Typography sx={{fontSize: 11, color: "#8A8AA0", flexShrink: 0}}>{campaign.startTime} – {campaign.endTime}</Typography>
      </Stack>
      <Box sx={{position: "relative", height: 8, borderRadius: "999px", bgcolor: "#EEF0FF"}}>
        <Box sx={{
          position: "absolute", top: 0, bottom: 0, left: `${(from / 1440) * 100}%`, width: `${((to - from) / 1440) * 100}%`,
          borderRadius: "999px", bgcolor: color.dot,
        }}/>
      </Box>
    </ButtonBase>
  );
};

export const CampaignAgenda = ({campaigns, onSelect}) => {
  const today = moment().startOf("day");

  const {todays, upcoming} = useMemo(() => ({
    todays: campaigns.filter(campaign => campaignRunsOn(campaign, today)),
    upcoming: campaigns
      .filter(campaign => campaignState(campaign) === "SCHEDULED")
      .sort((a, b) => campaignStart(a).diff(campaignStart(b)))
      .slice(0, 6),
  }), [campaigns]);

  return (
    <Stack sx={{height: "100%", minHeight: 0}}>
      <Box sx={{px: 2.5, pt: 2.5, pb: 1.5, bgcolor: "#F6F7FE"}}>
        <SectionLabel>Today</SectionLabel>
        <Typography sx={{fontSize: 20, fontWeight: 800, color: "#23264D", letterSpacing: "-0.02em"}}>
          {today.format("dddd, D MMMM")}
        </Typography>
        <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
          {todays.length === 0 ? "No campaigns calling today" : `${todays.length} ${todays.length === 1 ? "campaign" : "campaigns"} calling today`}
        </Typography>
      </Box>

      <Stack gap={2} sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 2.5, py: 1.5}}>
        {todays.length > 0 && (
          <Stack gap={1}>
            <Stack direction={"row"} justifyContent={"space-between"} sx={{px: 0.25}}>
              {HOUR_MARKS.map(hour => (
                <Typography key={hour} sx={{fontSize: 9, fontWeight: 600, color: "#B4B4C3"}}>
                  {String(hour).padStart(2, "0")}
                </Typography>
              ))}
            </Stack>
            {todays.map(campaign => <TodayRow key={campaign.id} campaign={campaign} onSelect={onSelect}/>)}
          </Stack>
        )}

        <Stack gap={1}>
          <SectionLabel>Coming up</SectionLabel>
          {upcoming.length === 0 && (
            <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>Nothing scheduled yet. Click a free day to create one.</Typography>
          )}
          {upcoming.map(campaign => {
            const color = campaignColor(campaign);
            const start = campaignStart(campaign);
            return (
              <ButtonBase key={campaign.id} onClick={() => onSelect(campaign)} sx={{
                width: "100%", justifyContent: "flex-start", gap: 1.25, textAlign: "left", p: 1, borderRadius: "14px",
                bgcolor: "#FAFAFD", transition: "background-color .15s ease",
                "&:hover": {bgcolor: "rgba(132, 145, 247, 0.12)"},
              }}>
                <Box sx={{
                  width: 42, height: 46, borderRadius: "12px", flexShrink: 0, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", bgcolor: color.bg, color: color.color,
                }}>
                  <Typography sx={{fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "inherit", lineHeight: 1}}>
                    {start.format("MMM").toUpperCase()}
                  </Typography>
                  <Typography sx={{fontSize: 17, fontWeight: 800, color: "inherit", lineHeight: 1.1}}>{start.date()}</Typography>
                </Box>
                <Box sx={{minWidth: 0}}>
                  <Typography noWrap sx={{fontSize: 13, fontWeight: 700, color: "#23264D"}}>{campaign.name}</Typography>
                  <Typography sx={{fontSize: 11, color: "#8A8AA0"}}>
                    {start.format("ddd")} · {campaign.startTime} – {campaign.endTime}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })}
        </Stack>
      </Stack>
    </Stack>
  );
};
